/**
 * @documental
 * @export useSearchFormRemoteTableDataSource
 * @import
 * import { useSearchFormRemoteTableDataSource } from "capitalrx-components-2.0/lib/general";
 */
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";

import { queryString } from "capitalrx-utilities";

import { TableIdProvider } from "./TableIdProvider";
import { RemoteTableDataSourceOptions, useRemoteTableDataSource } from "./useRemoteTableDataSource";

/**
 * Wrapper around `useRemoteTableDataSource`.
 *
 * The hook assumes the standard `/list` endpoint which conforms to the `caprx` interface for paginated APIs. It abstracts
 * away boilerplate implementation code used when a `<Table/>` component is paired with a search `<Form/>`; that
 * is -- it manages the decoding of a form's state as a url query string.
 *
 * @param url The URL to the remote data source.
 * @param idProvider A function which returns the unique identifier for a row.
 * @param options Pagination options config object (including optional authToken).
 * @param permittedFilterKeys Array of keys permitted for filtering (all allowed if empty)
 * @param queryParseOptions Parse options config object.
 * @returns An initialized data source.
 */
export const useSearchFormRemoteTableDataSource = <TRow extends object>(
  url: string,
  idProvider: TableIdProvider<TRow>,
  options: RemoteTableDataSourceOptions<TRow> = {},
  permittedFilterKeys: Array<string> = [],
  queryParseOptions: queryString.ParseOptions = {},
  excludeFilterKeys: Array<string> = [],
): ReturnType<typeof useRemoteTableDataSource<TRow>> => {
  // Pass all options through, including authToken if provided
  const dataSource = useRemoteTableDataSource<TRow>(url, idProvider, options);
  const location = useLocation();

  const { filter = {} } = options;
  const { clearFilter, doBatch } = dataSource;
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  /* Always re-apply base filters after clearing — even when there are no URL
  params — so that scoping filters (member identity, client, etc.) are
  never silently dropped when location.search transitions to empty. */
  useEffect(() => {
    dataSource.clearFilter();

    const urlParams = queryString.getQueryParams(location.search, queryParseOptions ?? {});
    let keys = Object.keys(urlParams);
    if (permittedFilterKeys.length > 0) {
      keys = keys.filter((key) => permittedFilterKeys.includes(key));
    }
    if (excludeFilterKeys.length > 0) {
      keys = keys.filter((key) => !excludeFilterKeys.includes(key));
    }

    doBatch(({ filterBy }) => {
      Object.entries(filter).forEach(([k, v]) => {
        if (k && v !== null && typeof v !== "undefined") {
          filterBy(k, v);
        }
      });

      keys.forEach((key) => {
        filterBy(key, urlParams[key]);
      });
    });
  }, [location.search, clearFilter, doBatch, filterKey]);

  return dataSource;
};
